import React, { useState, useEffect } from 'react';
import './UsersPage.css';
import { useOutletContext } from 'react-router-dom';
import axios from 'axios';
import { fetchCurrentUser, UserProfile, UserBadge } from '../services/userService';
import { getAuthToken } from '../Utils';
import { getPageTitle } from '../constants';
import BadgeItem from '../components/BadgeItem';

const UsersPage: React.FC = () => {
  const { translations } = useOutletContext<{ translations: Record<string, string> }>();
  const [user, setUser] = useState<UserProfile | null>(null);
  const [availableBadges, setAvailableBadges] = useState<UserBadge[]>([]);
  const [selectedBadgeId, setSelectedBadgeId] = useState<number | ''>('');
 
  useEffect(() => {
    document.title = getPageTitle('Diary');
  }, []);

  const getConfig = () => {
    const token = getAuthToken();
    const headers: Record<string, string> = {};
    if (token) {
      headers['Authorization'] = `Bearer ${token}`;
    }
    return { headers, withCredentials: true };
  };

  const loadAvailableBadges = async (userId: number) => {
    try {
      const response = await axios.get(`/api/badges?userId=${userId}&carry=false`, getConfig());
      setAvailableBadges(response.data || []);
    } catch (error) {
      console.error('Failed to fetch available badges:', error);
    }
  };

  useEffect(() => {
    const loadUser = async () => {
      try {
        const data = await fetchCurrentUser();
        setUser(data);
        loadAvailableBadges(data.id);
      } catch (error) {
        console.error('Failed to fetch user:', error);
      }
    };

    loadUser();
  }, []);

  const handleAddBadge = async () => {
    if (selectedBadgeId === '' || !user) return;
    try {
      const params = new URLSearchParams();
      params.append('badgeId', String(selectedBadgeId));
      await axios.post('/api/users-badges', params, getConfig());
      const data = await fetchCurrentUser();
      setUser(data);
      setSelectedBadgeId('');
      loadAvailableBadges(data.id);
    } catch (error) {
      console.error('Failed to add badge:', error);
    }
  };

  return (
    <>
      <div className="page-content">
        <div className="search-panel">
          <h3>{translations["user.profile"]}</h3>
          {user ? (
            <div className="profile-info">
              <div className="profile-avatar">
                {user.alias?.charAt(0).toUpperCase() || '?'}
              </div>
              <div className="profile-field">
                <span className="profile-label">{translations["user.info.alias"]}</span>
                <span className="profile-value">{user.alias}</span>
              </div>
              <div className="profile-field">
                <span className="profile-label">{translations["user.info.email"]}</span>
                <span className="profile-value">{user.email}</span>
              </div>
              {user.firstName && (
                <div className="profile-field">
                  <span className="profile-label">{translations["user.info.name"]}</span>
                  <span className="profile-value">
                    {[user.firstName, user.middleName, user.lastName].filter(Boolean).join(' ')}
                  </span>
                </div>
              )}
              {user.birthday && (
                <div className="profile-field">
                  <span className="profile-label">{translations["user.info.birthday"]}</span>
                  <span className="profile-value">{new Date(user.birthday).toISOString().split('T')[0]}</span>
                </div>
              )}
              {user.height && (
                <div className="profile-field">
                  <span className="profile-label">{translations["user.info.height"]}</span>
                  <span className="profile-value">{user.height}</span>
                </div>
              )}
              {user.weight && (
                <div className="profile-field">
                  <span className="profile-label">{translations["user.info.weight"]}</span>
                  <span className="profile-value">{user.weight}</span>
                </div>
              )}
              {user.bio && (
                <div className="profile-field">
                  <span className="profile-label">{translations["user.info.bio"]}</span>
                  <span className="profile-value">{user.bio}</span>
                </div>
              )}
              {user.sex && (
                <div className="profile-field">
                  <span className="profile-label">{translations["user.sex"]}</span>
                  <span className="profile-value">{user.sex}</span>
                </div>
              )}
            </div>
          ) : (
            <p className="no-results">{translations["loading"]}</p>
          )}
        </div>

        <div className="results-section">
          <h2>{translations["user.sports"]}</h2>
          {user && user.badges.length > 0 ? (
            <div className="badges-list">
              {user.badges.map((badge) => (
                <BadgeItem
                  key={badge.id}
                  name={badge.name}
                  type={badge.type}
                />
              ))}
            </div>
          ) : (
            <p className="no-results">
              {translations["user.noSports"]}
            </p>
          )}
          {availableBadges.length > 0 && (
            <div className="badge-add-form">
              <select
                className="form-select"
                value={selectedBadgeId}
                onChange={(e) => setSelectedBadgeId(e.target.value ? Number(e.target.value) : '')}
              >
                <option value="">{translations["badge.select"]}</option>
                {availableBadges.map((badge) => (
                  <option key={badge.id} value={badge.id}>{badge.name}</option>
                ))}
              </select>
              <button className="search-button" onClick={handleAddBadge}>
                {translations["button.add"]}
              </button>
            </div>
          )}
        </div>
      </div>
    </>
  );
};

export default UsersPage;